"use client";

import React, {
  createContext,
  useContext,
  useState,
  ReactNode,
  useCallback,
  useEffect,
} from "react";
import {
  Customer,
  Vehicle,
  OneTimePackage,
  SubscriptionPlan,
  AddOn,
  Appointment,
  Subscription,
  Invoice,
  Staff,
  Activity,
  ServiceTypeSetting,
  PaymentModeSetting,
  CustomerCategorySetting,
  CustomerTypeSetting,
} from "@/types";
import { AllProviders } from "./Providers";
import { useCustomers } from "./CustomerContext";
import { useVehicles } from "./VehicleContext";
import { usePackages } from "./PackageContext";
import {
  useAppointments,
  UpdateAppointmentOptions,
} from "./AppointmentContext";
import { useSubscriptions } from "./SubscriptionContext";
import { useInvoices } from "./InvoiceContext";
import { useActivities } from "./ActivityContext";
import { useSettings } from "./SettingsContext";
import { useAuth } from "./AuthContext";
import { userService } from "@/services/userService";
import { StaffApiResponse } from "@/types";
import { toast } from "sonner";

interface AppContextType {
  // State
  customers: Customer[];
  vehicles: Vehicle[];
  oneTimePackages: OneTimePackage[];
  subscriptionPlans: SubscriptionPlan[];
  addOns: AddOn[];
  packagesLoading: boolean;
  packagesError: string | null;
  appointments: Appointment[];
  appointmentsLoading: boolean;
  appointmentsError: string | null;
  subscriptions: Subscription[];
  invoices: Invoice[];
  staff: Staff[];
  activities: Activity[];
  serviceTypes: ServiceTypeSetting[];
  paymentModes: PaymentModeSetting[];
  customerCategories: CustomerCategorySetting[];
  customerTypes: CustomerTypeSetting[];
  
  // Appointment refresh
  refreshAppointments: () => Promise<void>;
  fetchAppointmentsWithFilters: (params?: import("@/types/appointment.api.types").AppointmentQueryParams) => Promise<void>;

  // Customer refresh
  refreshCustomers: () => Promise<void>;

  // Customer operations
  addCustomer: (
    customer: Omit<
      Customer,
      | "id"
      | "createdAt"
      | "totalSpending"
      | "customerType"
      | "customerCategory"
      | "customerStatus"
      | "numberOfInteriorCleaning"
      | "serviceProviders"
    > &
      Partial<
        Pick<
          Customer,
          | "customerType"
          | "customerCategory"
          | "customerStatus"
          | "numberOfInteriorCleaning"
          | "serviceProviders"
        >
      >
  ) => Customer;
  updateCustomer: (id: string, customer: Partial<Customer>) => void;
  deleteCustomer: (id: string) => void;
  getCustomerById: (id: string) => Customer | undefined;

  // Vehicle operations
  addVehicle: (vehicle: Omit<Vehicle, "id" | "totalServices">) => Promise<Vehicle>;
  updateVehicle: (id: string, vehicle: Partial<Vehicle>) => void;
  deleteVehicle: (id: string) => void;
  getVehicleById: (id: string) => Vehicle | undefined;
  getVehiclesByCustomerId: (customerId: string) => Vehicle[];

  // Package operations
  addOneTimePackage: (pkg: Omit<OneTimePackage, "id">) => Promise<OneTimePackage>;
  updateOneTimePackage: (id: string, pkg: Partial<OneTimePackage>) => void;
  deleteOneTimePackage: (id: string) => Promise<void>;

  // Subscription Plan operations
  addSubscriptionPlan: (plan: Omit<SubscriptionPlan, "id">) => SubscriptionPlan;
  updateSubscriptionPlan: (id: string, plan: Partial<SubscriptionPlan>) => void;

  // Add-on operations
  addAddOn: (addOn: Omit<AddOn, "id">) => AddOn;
  updateAddOn: (id: string, addOn: Partial<AddOn>) => void;
  deleteAddOn: (id: string) => void;

  // Appointment operations
  addAppointment: (appointment: Omit<Appointment, "id">) => Promise<Appointment>;
  updateAppointment: (
    id: string,
    appointment: Partial<Appointment>,
    options?: UpdateAppointmentOptions,
  ) => Promise<void>;
  bulkAssignStaff: (
    appointmentIds: string[],
    staffId: string | null,
    staffName?: string | null,
  ) => Promise<void>;
  deleteAppointment: (id: string) => Promise<void>;
  getAppointmentById: (id: string) => Appointment | undefined;

  // Subscription operations
  addSubscription: (subscription: Omit<Subscription, "id">) => Subscription;
  updateSubscription: (id: string, subscription: Partial<Subscription>) => void;
  deleteSubscription: (id: string) => void;
  getSubscriptionById: (id: string) => Subscription | undefined;
  getActiveSubscriptionsByCustomerId: (customerId: string) => Subscription[];
  getSubscriptionsByCustomerId: (customerId: string) => Subscription[];

  // Invoice operations
  addInvoice: (invoice: Omit<Invoice, "id">) => Invoice;
  updateInvoice: (id: string, invoice: Partial<Invoice>) => void;
  deleteInvoice: (id: string) => void;
  getInvoiceById: (id: string) => Invoice | undefined;

  // Staff operations
  addStaff: (staff: Omit<Staff, "id" | "totalJobs">) => Staff;
  updateStaff: (id: string, staff: Partial<Staff>) => void;
  deleteStaff: (id: string) => void;
  getStaffById: (id: string) => Staff | undefined;

  // Activity operations
  addActivity: (activity: Omit<Activity, "id" | "createdAt">) => void;

  // Settings operations
  addServiceType: (
    serviceType: Omit<ServiceTypeSetting, "id" | "createdAt">
  ) => ServiceTypeSetting;
  updateServiceType: (
    id: string,
    serviceType: Partial<ServiceTypeSetting>
  ) => void;
  deleteServiceType: (id: string) => void;

  addPaymentMode: (
    paymentMode: Omit<PaymentModeSetting, "id" | "createdAt">
  ) => PaymentModeSetting;
  updatePaymentMode: (
    id: string,
    paymentMode: Partial<PaymentModeSetting>
  ) => void;
  deletePaymentMode: (id: string) => void;

  addCustomerCategory: (
    category: Omit<CustomerCategorySetting, "id" | "createdAt">
  ) => CustomerCategorySetting;
  updateCustomerCategory: (
    id: string,
    category: Partial<CustomerCategorySetting>
  ) => void;
  deleteCustomerCategory: (id: string) => void;

  addCustomerType: (
    type: Omit<CustomerTypeSetting, "id" | "createdAt">
  ) => CustomerTypeSetting;
  updateCustomerType: (id: string, type: Partial<CustomerTypeSetting>) => void;
  deleteCustomerType: (id: string) => void;
}

const AppContext = createContext<AppContextType | undefined>(undefined);

// Helper function to transform StaffApiResponse to Staff
function transformStaff(apiStaff: StaffApiResponse): Staff {
  const staffDetail = apiStaff.Staff?.[0];
  return {
    id: apiStaff.id,
    name: apiStaff.name,
    phone: apiStaff.phone,
    email: apiStaff.email || "",
    password: "", // Never store password
    role: (apiStaff.role?.toLowerCase() || "technician") as "technician" | "manager",
    status: staffDetail?.isDeleted ? "inactive" : "active",
    joinedDate: new Date(apiStaff.createdAt),
    totalJobs: 0, // Calculate from appointments if needed
  };
}

// Wrapper component that uses all the new contexts
function AppContextWrapper({ children }: { children: ReactNode }) {
  // Get all context values
  const customersContext = useCustomers();
  const vehiclesContext = useVehicles();
  const packagesContext = usePackages();
  const appointmentsContext = useAppointments();
  const subscriptionsContext = useSubscriptions();
  const invoicesContext = useInvoices();
  const activitiesContext = useActivities();
  const settingsContext = useSettings();
  const { isAuthenticated, isLoading: authLoading, user } = useAuth();

  // Staff state (not yet in a separate context, keeping here for backward compatibility)
  const [staff, setStaff] = useState<Staff[]>([]);

  // Fetch staff on mount, but only if authenticated and user is admin or manager
  useEffect(() => {
    // Wait for auth to finish loading, then check if authenticated
    if (!authLoading && isAuthenticated && user) {
      // Only fetch staff if user is ADMIN or manager (technicians don't have permission)
      const userRole = user.role?.toUpperCase();
      const isAdminOrManager = userRole === 'ADMIN' || userRole === 'MANAGER' || user.role === 'manager';
      
      if (isAdminOrManager) {
        const fetchStaff = async () => {
          try {
            const data = await userService.getAllStaffs();
            const transformed = data.map(transformStaff);
            transformed.sort((a, b) => a.name.localeCompare(b.name));
            setStaff(transformed);
          } catch (error: any) {
            console.error("Error fetching staff:", error);
            // Only show error toast for admin/manager users, silently fail for others
            toast.error("Failed to fetch staff");
          }
        };
        fetchStaff();
      } else {
        // For staff members, set empty array (they don't need staff list)
        setStaff([]);
      }
    }
  }, [authLoading, isAuthenticated, user]);

  // Staff operations (keeping in wrapper for now)
  const addStaff = useCallback((staffData: Omit<Staff, "id" | "totalJobs">) => {
    let newStaff: Staff;
    setStaff((prev) => {
      newStaff = {
        ...staffData,
        id: `ST${String(prev.length + 1).padStart(3, "0")}`,
        totalJobs: 0,
      };
      return [newStaff, ...prev];
    });
    return newStaff!;
  }, []);

  const updateStaff = useCallback((id: string, staffData: Partial<Staff>) => {
    setStaff((prev) =>
      prev.map((s) => (s.id === id ? { ...s, ...staffData } : s))
    );
  }, []);

  const deleteStaff = useCallback((id: string) => {
    setStaff((prev) => prev.filter((s) => s.id !== id));
  }, []);

  const getStaffById = useCallback(
    (id: string) => {
      return staff.find((s) => s.id === id);
    },
    [staff]
  );

  // Compose the value object matching the original interface
  const value: AppContextType = {
    // State from contexts
    customers: customersContext.customers,
    vehicles: vehiclesContext.vehicles,
    oneTimePackages: packagesContext.oneTimePackages,
    subscriptionPlans: packagesContext.subscriptionPlans,
    addOns: packagesContext.addOns,
    packagesLoading: packagesContext.loading,
    packagesError: packagesContext.error,
    appointments: appointmentsContext.appointments,
    appointmentsLoading: appointmentsContext.loading,
    appointmentsError: appointmentsContext.error,
    subscriptions: subscriptionsContext.subscriptions,
    invoices: invoicesContext.invoices,
    staff,
    activities: activitiesContext.activities,
    serviceTypes: settingsContext.serviceTypes,
    paymentModes: settingsContext.paymentModes,
    customerCategories: settingsContext.customerCategories,
    customerTypes: settingsContext.customerTypes,

    // Operations from contexts
    refreshAppointments: appointmentsContext.refreshAppointments,
    fetchAppointmentsWithFilters: appointmentsContext.fetchAppointmentsWithFilters,
    refreshCustomers: customersContext.refreshCustomers,
    addCustomer: customersContext.addCustomer,
    updateCustomer: customersContext.updateCustomer,
    deleteCustomer: customersContext.deleteCustomer,
    getCustomerById: customersContext.getCustomerById,
    addVehicle: vehiclesContext.addVehicle,
    updateVehicle: vehiclesContext.updateVehicle,
    deleteVehicle: vehiclesContext.deleteVehicle,
    getVehicleById: vehiclesContext.getVehicleById,
    getVehiclesByCustomerId: vehiclesContext.getVehiclesByCustomerId,
    addOneTimePackage: packagesContext.addOneTimePackage,
    updateOneTimePackage: packagesContext.updateOneTimePackage,
    deleteOneTimePackage: packagesContext.deleteOneTimePackage,
    addSubscriptionPlan: packagesContext.addSubscriptionPlan,
    updateSubscriptionPlan: packagesContext.updateSubscriptionPlan,
    addAddOn: packagesContext.addAddOn,
    updateAddOn: packagesContext.updateAddOn,
    deleteAddOn: packagesContext.deleteAddOn,
    addAppointment: appointmentsContext.addAppointment,
    updateAppointment: appointmentsContext.updateAppointment,
    bulkAssignStaff: appointmentsContext.bulkAssignStaff,
    deleteAppointment: appointmentsContext.deleteAppointment,
    getAppointmentById: appointmentsContext.getAppointmentById,
    addSubscription: subscriptionsContext.addSubscription,
    updateSubscription: subscriptionsContext.updateSubscription,
    deleteSubscription: subscriptionsContext.deleteSubscription,
    getSubscriptionById: subscriptionsContext.getSubscriptionById,
    getActiveSubscriptionsByCustomerId: subscriptionsContext.getActiveSubscriptionsByCustomerId,
    getSubscriptionsByCustomerId: subscriptionsContext.getSubscriptionsByCustomerId,
    addInvoice: invoicesContext.addInvoice,
    updateInvoice: invoicesContext.updateInvoice,
    deleteInvoice: invoicesContext.deleteInvoice,
    getInvoiceById: invoicesContext.getInvoiceById,
    addStaff,
    updateStaff,
    deleteStaff,
    getStaffById,
    addActivity: activitiesContext.addActivity,
    addServiceType: settingsContext.addServiceType,
    updateServiceType: settingsContext.updateServiceType,
    deleteServiceType: settingsContext.deleteServiceType,
    addPaymentMode: settingsContext.addPaymentMode,
    updatePaymentMode: settingsContext.updatePaymentMode,
    deletePaymentMode: settingsContext.deletePaymentMode,
    addCustomerCategory: settingsContext.addCustomerCategory,
    updateCustomerCategory: settingsContext.updateCustomerCategory,
    deleteCustomerCategory: settingsContext.deleteCustomerCategory,
    addCustomerType: settingsContext.addCustomerType,
    updateCustomerType: settingsContext.updateCustomerType,
    deleteCustomerType: settingsContext.deleteCustomerType,
  };

  return <AppContext.Provider value={value}>{children}</AppContext.Provider>;
}

export function AppProvider({ children }: { children: ReactNode }) {
  return (
    <AllProviders>
      <AppContextWrapper>{children}</AppContextWrapper>
    </AllProviders>
  );
}

export function useApp() {
  const context = useContext(AppContext);
  if (context === undefined) {
    throw new Error("useApp must be used within an AppProvider");
  }
  return context;
}
